<template>
  <div>
    <div class="d-flex mb-3 align-items-end flex-wrap" style="gap: 0.75rem;">
      <b-input-group style="max-width: 300px;">
        <b-input-group-prepend is-text>
          <b-icon icon="search"></b-icon>
        </b-input-group-prepend>
        <b-form-input
          :value="searchQuery"
          :placeholder="$t('mediaLibrary.searchPlaceholder')"
          @update="$emit('search', $event)"
        ></b-form-input>
        <b-input-group-append>
          <b-button variant="link" @click="$emit('clear-search')" v-if="searchQuery">
            <b-icon icon="x"></b-icon>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <div class="d-flex align-items-end" style="gap: 0.5rem;">
        <div>
          <label class="mb-1 small text-muted">{{ $t('mediaLibrary.dateFrom') }}</label>
          <b-form-datepicker
            :value="dateFrom"
            :placeholder="$t('mediaLibrary.dateFrom')"
            size="sm"
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            :locale="$i18n.locale"
            @input="$emit('date-from-change', $event)"
          ></b-form-datepicker>
        </div>
        <div>
          <label class="mb-1 small text-muted">{{ $t('mediaLibrary.dateTo') }}</label>
          <b-form-datepicker
            :value="dateTo"
            :placeholder="$t('mediaLibrary.dateTo')"
            size="sm"
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            :locale="$i18n.locale"
            @input="$emit('date-to-change', $event)"
          ></b-form-datepicker>
        </div>
        <b-button
          v-if="dateFrom || dateTo"
          variant="link"
          size="sm"
          @click="$emit('clear-dates')"
          class="pb-2"
        >
          <b-icon icon="x-circle"></b-icon>
        </b-button>
      </div>
    </div>
    <div v-if="loading" class="text-center my-5">
      <b-spinner small type="grow" />
      {{ $t('mediaLibrary.loading') }}
    </div>
    <div v-else-if="images.length === 0" class="text-center my-5 text-muted">
      {{ $t('mediaLibrary.empty') }}
    </div>
    <div v-else>
      <div class="image-library-grid">
        <div
          v-for="img in images"
          :key="img.image_id"
          class="image-library-item"
        >
          <div class="image-thumb-container">
              <b-button
                size="sm"
                variant="danger"
                class="delete-btn"
                :title="$t('mediaLibrary.delete')"
                @click.stop="$emit('delete-image', img)"
              >
                <b-icon icon="trash"></b-icon>
              </b-button>
            <video v-if="isVideoFile(img.url)" class="img-thumbnail video-thumb" controls muted preload="metadata">
              <source :src="img.url" type="video/mp4">
            </video>
            <audio v-else-if="isAudioFile(img.url)" :src="img.url" class="img-thumbnail" controls></audio>
            <iframe v-else-if="isPdfFile(img.url)" :src="img.url" class="img-thumbnail" frameborder="0"></iframe>
            <img v-else :src="cloudImageUrl(img.url)" :alt="img.name" class="img-thumbnail" />
          </div>
          <div class="image-info">
            <div class="image-name-row">
              <div class="image-name" :title="img.name" @click="copyName(img.name)">{{ img.name }}</div>
              <b-button
                size="sm"
                variant="link"
                class="download-btn"
                @click="$emit('download', img)"
                :title="$t('mediaLibrary.download')"
              >
                <b-icon icon="download" aria-hidden="true"></b-icon>
              </b-button>
            </div>
            <div class="image-meta">
              <span>{{ formatDate(img.created_at) }}</span>
              <span v-if="getFileSize(img)">· {{ getFileSize(img) }}</span>
            </div>
          </div>
        </div>
      </div>
      <b-pagination
        v-if="totalPages > 1"
        v-model="localPage"
        :per-page="1"
        :total-rows="totalPages"
        :limit="5"
        size="md"
        @change="onPageChange"
        class="library-pagination"
      />
    </div>
  </div>
</template>

<script>
import { BPagination } from 'bootstrap-vue';

export default {
  name: 'MediaLibraryContent',
  components: {
    BPagination,
  },
  props: {
    images: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    totalPages: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    searchQuery: { type: String, default: '' },
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null },
  },
  data() {
    return {
      localPage: this.currentPage,
    };
  },
  watch: {
    currentPage(val) {
      if (val !== this.localPage) {
        this.localPage = val;
      }
    },
  },
  methods: {
    onPageChange(page) {
      this.localPage = page;
      this.$emit('page-change', page);
    },
    cloudImageUrl(url) {
      if (!url) return '';
      const bunnyUrl = this.$transformS3ToBunnyUrl(url);
      const separator = bunnyUrl.includes('?') ? '&' : '?';
      return `${bunnyUrl}${separator}quality=95&width=262`;
    },
    isVideoFile(url) {
      if (!url) return false;
      const videoExtensions = ['.mp4', '.webm', '.avi', '.mov', '.mkv'];
      const lowerUrl = url.toLowerCase();
      return videoExtensions.some((ext) => {
        const extIndex = lowerUrl.indexOf(ext);
        if (extIndex === -1) return false;
        const afterExt = lowerUrl.substring(extIndex + ext.length);
        return afterExt === '' || afterExt.startsWith('?') || afterExt.startsWith('#');
      });
    },
    isAudioFile(url) {
      if (!url) return false;
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
      const lowerUrl = url.toLowerCase();
      return audioExtensions.some((ext) => {
        const extIndex = lowerUrl.indexOf(ext);
        if (extIndex === -1) return false;
        const afterExt = lowerUrl.substring(extIndex + ext.length);
        return afterExt === '' || afterExt.startsWith('?') || afterExt.startsWith('#');
      });
    },
    isPdfFile(url) {
      if (!url) return false;
      const lowerUrl = url.toLowerCase();
      const extIndex = lowerUrl.indexOf('.pdf');
      if (extIndex === -1) return false;
      const afterExt = lowerUrl.substring(extIndex + 4);
      return afterExt === '' || afterExt.startsWith('?') || afterExt.startsWith('#');
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    getFileSize(img) {
      const size = img.metadata?.size || img.metadata?.fileSize || img.metadata?.file_size;
      if (!size) return null;
      return this.formatFileSize(size);
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    async copyName(name) {
      try {
        await navigator.clipboard.writeText(name);
        this.$bvToast.toast(name, {
          title: this.$t('mediaLibrary.copied'),
          variant: 'success',
          solid: true,
          autoHideDelay: 2000,
          noCloseButton: true,
        });
      } catch (e) {
        console.error('Copy failed:', e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.image-library-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.image-library-item {
  width: 262px;
  display: flex;
  flex-direction: column;
  background: #53575B;
  overflow: hidden;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
}

.image-thumb-container {
  position: relative;
  background: #777C7E;
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.delete-btn {
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  z-index: 3;
  color: #fff;

  &:hover {
    color: #fff;
  }
}

.img-thumbnail {
  max-width: 100%;
  max-height: 180px;
  display: block;
  margin: 0 auto;
  object-fit: cover;
  border: none;
  border-radius: 0;
}

video.video-thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

audio.img-thumbnail {
  height: 100%;
}

.image-info {
  padding: 0.5rem 0.75rem;
  flex-grow: 1;
}

.image-name-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.image-name {
  font-size: 0.85em;
  color: #fff;
  flex: 1;
  min-width: 0;
  text-align: left;
  word-break: break-word;
  cursor: pointer;

  &:hover {
    color: #28dc8e;
  }
}

.download-btn {
  color: #fff;
  padding: 0.15rem 0.3rem;
  flex-shrink: 0;

  &:hover {
    color: #28dc8e;
  }
}

.image-meta {
  font-size: 0.75em;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.2em;
  text-align: left;
}

.library-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5em;
  gap: 1em;
}

::v-deep .pagination .page-item .page-link {
  background-color: #53575B;
  border-color: #53575B;
  color: #fff;
}

::v-deep .pagination .page-item.active .page-link {
  background-color: #777C7E !important;
  border-color: #777C7E !important;
  color: #fff !important;
  font-weight: 700;
}

::v-deep .pagination .page-item.disabled .page-link {
  background-color: #3a3d40;
  border-color: #3a3d40;
  color: rgba(255, 255, 255, 0.4);
}

::v-deep .pagination .page-link:hover {
  background-color: #777C7E;
  border-color: #777C7E;
  color: #fff;
}

::v-deep .input-group-text {
  background-color: #53575B;
  border-color: #53575B;
  color: #fff;
}

::v-deep .b-form-datepicker {
  background-color: #53575B;
  border-color: #53575B;
  border-radius: 0.25rem;

  label {
    background-color: #53575B;
    color: #fff;
    border-color: #53575B;
  }
}
</style>
